import React, { useEffect, useState, useCallback, useRef } from 'react';
import { addBusinessDays } from 'date-fns';
import { useForm, FormProvider, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { V1RecurringRuleInterval } from '@fintronners/react-api/src';
import useRecurringRules from '@fintronners/react-widgets/src/hooks/useRecurringRules';
import useExternalAccounts, {
  AccountType,
} from '@fintronners/react-widgets/src/hooks/useExternalAccounts';
import useTransferSubmission from '@fintronners/react-widgets/src/hooks/transfers/useTransferSubmission';
import { useTransfer } from '@/hooks/api/useTransfer';
import { useDepositInfo } from '@/hooks/api/useDepositInfo';
import { useRecurringTransfer } from '@/hooks/api/useRecurringTransfer';
import { useWithdrawableAmount } from '@/hooks/api/useWithdrawableAmount';
import { useWithdrawal } from '@/hooks/api/useWithdrawal';
import { useSidePanel } from '@/context/SidePanelContext';
import {
  TransferType,
  DepositType,
  TransferFormData,
  transferSchema,
  TransferValidationContext,
} from '@fintronners/internal-core/src/transfer/schema';
import { TransferSidePanelForm } from './TransferSidePanelForm';
import { TransferSidePanelConfirmation } from './TransferSidePanelConfirmation';
import { TransferSidePanelSuccess } from './TransferSidePanelSuccess';
import { ErrorSidePanel } from '../ErrorSidePanel/ErrorSidePanel';
import { useIsIRAAccount } from '@/hooks/utils/useCurrentAccountType';
import useFeatureFlags from '@/hooks/config/useFeatureFlags';
import { useContributions } from '@/hooks/api/useContributions';
import FinTronConfig from '@fintronners/react-config/src/Config/FinTronConfig';

interface TransferSidePanelProps {
  selectedTaxYear?: number;
}

const { depositMin, withdrawalMin, withdrawalMax, dailyDepositLimit } =
  FinTronConfig.selfDirected.transfers;

export const TransferSidePanel = ({ selectedTaxYear }: TransferSidePanelProps) => {
  const [hasError, setHasError] = useState(false);
  const [shouldDisableEntireForm, setShouldDisableEntireForm] = useState(false);
  const [formTransferType, setFormTransferType] = useState<TransferType>();
  const [isAccountValid, setIsAccountValid] = useState(true);

  const { tenants } = useFeatureFlags();
  const { isAlight } = tenants;

  const { currentFlowStep, goTo, goBack, setShowBackLink, setShowCloseButton } = useSidePanel();

  const { data: depositInfo, loading: isDepositInfoLoading } = useDepositInfo();

  const { firstExternalAccount, fetchingAccounts } = useExternalAccounts(
    AccountType.BROKERAGE,
    undefined,
    false,
    isAlight,
  );
  const { data: withdrawableAmount, loading: isWithdrawableAmountLoading } =
    useWithdrawableAmount();
  const { createTransfer, fetchSummary, summary, isSummaryLoading } = useTransfer();
  const { recurringRules, loading: isRecurringRulesLoading } = useRecurringRules();
  const { createRecurringDeposit } = useRecurringTransfer();
  const { createWithdrawal, isPrematureWithdrawal } = useWithdrawal();
  const isIRAAccount = useIsIRAAccount();

  const targetRecurringRule = recurringRules.at(0)?.node;
  const { amount: existingAmount } = targetRecurringRule || {};

  const availableInstantDepositLimit =
    depositInfo?.userAccountPortfolios.edges?.[0]?.node?.availableInstantDeposit;

  const isLoading =
    isDepositInfoLoading ||
    fetchingAccounts ||
    isRecurringRulesLoading ||
    isWithdrawableAmountLoading;

  const availableWithdrawalAmountValue =
    parseFloat(
      withdrawableAmount?.userAccountPortfolios?.edges?.[0]?.node?.assetBalances?.edges?.[0]?.node
        ?.balances?.withdrawable ?? '0',
    ) || 0;

  const availableDailyDepositLimitValue =
    parseFloat(depositInfo?.userAccountPortfolios.edges?.[0]?.node?.availableDailyDeposit ?? '0') ||
    dailyDepositLimit;

  const availableInstantDepositLimitValue =
    parseFloat(
      depositInfo?.userAccountPortfolios.edges?.[0]?.node?.availableInstantDeposit ?? '0',
    ) || 0;

  const validationContextRef = useRef<TransferValidationContext | null>(null);
  const contributionsYearRef = useRef<number | undefined>(
    selectedTaxYear || new Date().getFullYear(),
  );

  const { overall, refetchContributions } = useContributions({
    taxYear: contributionsYearRef.current,
    skip: !isIRAAccount,
  });

  validationContextRef.current = {
    availableWithdrawalAmount: availableWithdrawalAmountValue,
    contributionLimitLeft: isIRAAccount ? overall?.limitLeft : undefined,
    dailyDepositLimit: availableDailyDepositLimitValue,
    instantDepositLimit: availableInstantDepositLimitValue,
    isIRAAccount,
    isAccountValid: !!firstExternalAccount && isAccountValid,
    account: firstExternalAccount,
    depositMin,
    withdrawalMin,
    withdrawalMax,
  };

  const resolver = useCallback(async (values: TransferFormData, _context: any, options: any) => {
    return yupResolver(transferSchema)(values, validationContextRef.current!, options);
  }, []);

  const form = useForm<TransferFormData>({
    resolver,
    mode: 'onChange',
    disabled: isLoading || (isIRAAccount && shouldDisableEntireForm),
  });

  useEffect(() => {
    const defaultFormValues = transferSchema.getDefault({
      context: validationContextRef.current || undefined,
    });
    const tomorrow = addBusinessDays(new Date(), 1).toISOString();

    form.reset(
      {
        ...defaultFormValues,
        interval: recurringRules.at(0)?.node?.interval ?? V1RecurringRuleInterval.EveryWeek,
        selectedStartDate: recurringRules.at(0)?.node?.next ?? tomorrow,
        availableWithdrawalAmount:
          withdrawableAmount?.userAccountPortfolios?.edges?.[0]?.node?.assetBalances?.edges?.[0]
            ?.node?.balances?.withdrawable ?? defaultFormValues.availableWithdrawalAmount,
        taxYear: isIRAAccount ? selectedTaxYear || new Date().getFullYear() : undefined,
      },
      {
        keepErrors: false,
        keepDirty: false,
        keepTouched: false,
      },
    );
  }, [recurringRules, withdrawableAmount, isIRAAccount, selectedTaxYear]);

  const transferType = useWatch({ control: form.control, name: 'transferType' });
  const depositType = useWatch({ control: form.control, name: 'depositType' });
  const amount = useWatch({ control: form.control, name: 'amount' });
  const taxYear = useWatch({ control: form.control, name: 'taxYear' });

  // Update contributions year ref and refetch when form's taxYear changes
  useEffect(() => {
    if (taxYear !== undefined && taxYear !== contributionsYearRef.current) {
      contributionsYearRef.current = taxYear;
      refetchContributions({ year: taxYear.toString() });
    }
  }, [taxYear, refetchContributions]);

  // Revalidate amount when transfer type changes or contribution limits update
  useEffect(() => {
    if (transferType || (isIRAAccount && overall?.limitLeft !== undefined)) {
      form.trigger('amount');
    }
  }, [transferType, overall?.limitLeft, isIRAAccount]);

  useEffect(() => {
    if (depositType === DepositType.ONE_TIME) {
      form.setValue('amount', '0');
      return;
    }

    form.setValue('amount', existingAmount || '0');
  }, [depositType, existingAmount]);

  const handleToggleFormState = useCallback(
    () => setShouldDisableEntireForm(!shouldDisableEntireForm),
    [],
  );

  const handleOnContinue = async () => {
    try {
      await fetchSummary({
        amount,
        externalAccountUid: firstExternalAccount?.id,
      });
      goTo(1);
    } catch (error) {
      setHasError(true);
    }
  };

  const { performTransfer, isSubmitting } = useTransferSubmission({
    firstExternalAccount,
    createTransfer,
    createRecurringDeposit,
    createWithdrawal,
    onSuccess: async () => {
      if (isIRAAccount && refetchContributions) {
        await refetchContributions();
      }
      goTo(2);
    },
    onError: () => {
      setHasError(true);
      setShowCloseButton(true);
    },
    onLoadingChange: (loading) => {
      if (!loading) {
        setShowBackLink(false);
      }
    },
  });

  const handleConfirmTransfer = useCallback(async () => {
    const values = form.getValues();
    await performTransfer(values);
  }, [performTransfer, form]);

  if (hasError) {
    return (
      <ErrorSidePanel
        onBack={() => {
          setHasError(false);
          setShowCloseButton(false);
        }}
      />
    );
  }

  return (
    <FormProvider {...form}>
      <form>
        {currentFlowStep === 0 && (
          <TransferSidePanelForm
            isLoading={isLoading}
            isPrematureWithdrawal={isPrematureWithdrawal}
            availableInstantDepositLimit={availableInstantDepositLimit}
            account={firstExternalAccount}
            setIsAccountValid={setIsAccountValid}
            onContinue={handleOnContinue}
            handleToggleFormState={handleToggleFormState}
            setFormTransferType={setFormTransferType}
          />
        )}

        {currentFlowStep === 1 && (
          <TransferSidePanelConfirmation
            summary={summary}
            isPrematureWithdrawal={isPrematureWithdrawal}
            isSummaryLoading={isSummaryLoading}
            account={firstExternalAccount}
            isSubmitting={isSubmitting}
            onContinue={form.handleSubmit(handleConfirmTransfer)}
            onBack={goBack}
          />
        )}

        {currentFlowStep === 2 && <TransferSidePanelSuccess formTransferType={formTransferType} />}
      </form>
    </FormProvider>
  );
};
